import type { FlexPromotion, Tag } from '@commercelayer/sdk'

/**
 * Represents detailed promotion information
 *
 * @typedef {Object} PromotionInformation
 * @property {string} id - Unique identifier for the promotion
 * @property {string} name - Name of the promotion
 * @property {string} starts_at - When the promotion starts
 * @property {string} expires_at - When the promotion expires
 * @property {number} total_usage_limit - Maximum number of times the promotion can be used
 * @property {number} total_usage_count - How many times the promotion has been used
 * @property {Object} metadata - Additional metadata for the promotion
 * @property {Tag[]} tags - Tags associated with the promotion
 * @property {string} reference - Reference for the promotion
 * @property {boolean} isActive - Whether the promotion is currently active
 */
export type PromotionInformation = {
  id: string
  name: string
  starts_at: string
  expires_at: string
  total_usage_limit: number
  total_usage_count: number
  metadata: Record<string, any>
  tags: Tag[]
  reference: string
  isActive: boolean
}

/**
 * Composable for managing flex promotions
 *
 * Provides functionality to fetch and process flex promotions, specifically for free shipping
 *
 * @returns {Object} Promotions related utilities
 * @property {ComputedRef<PromotionInformation[]>} freeShippingPromotions - Available free shipping promotions for current market
 * @property {ComputedRef<PromotionInformation | null>} activeFreeShippingPromotion - Currently active free shipping promotion
 * @property {Function} fetchFreeShippingPromotionsByMarket - Fetch free shipping promotions for a specific market
 * @property {Function} isPromotionActive - Check if a promotion is currently active
 *
 * @example
 * const {
 *   freeShippingPromotions,
 *   activeFreeShippingPromotion,
 *   fetchFreeShippingPromotionsByMarket,
 *   isPromotionActive
 * } = usePromotions()
 */
export const usePromotions = () => {
  const configs = useConfigs()
  const market = computed(() => configs.value.cl_market_code!)

  /**
   * Type definition for promotions response
   * @typedef {Array<FlexPromotion>} PromotionsResponse
   */
  type PromotionsResponse = FlexPromotion[]

  /**
   * Maps raw flex promotions to a more user-friendly promotion information format
   *
   * @param {PromotionsResponse} promotions - Raw promotions from API
   * @returns {PromotionInformation[]} Processed and formatted promotion information
   */
  const mapPromotions = (
    promotions: PromotionsResponse
  ): PromotionInformation[] => {
    return promotions.map(promotion => ({
      id: promotion.id ?? '',
      name: promotion.name ?? '',
      starts_at: promotion.starts_at ?? '',
      expires_at: promotion.expires_at ?? '',
      total_usage_limit: promotion.total_usage_limit ?? 0,
      total_usage_count: promotion.total_usage_count ?? 0,
      metadata: promotion.metadata ?? {},
      tags: promotion.tags ?? [],
      reference: promotion.reference ?? '',
      isActive: promotion.active ?? false,
    }))
  }

  /**
   * Lazy fetch for free shipping promotions based on current market
   *
   * @type {Ref<PromotionsResponse>}
   */
  const { data } = useLazyFetch<PromotionsResponse>(
    `/api/cl/getPromotions/${market.value}`,
    {
      key: `usePromotions-freeShippingPromotions-${market.value}`,
      getCachedData: (key, nuxtApp) => nuxtApp.payload.data[key],
      default: () => [],
      dedupe: 'defer',
    }
  )

  /**
   * Fetches free shipping promotions for a specific market
   *
   * @param {string} market - Market code to fetch promotions for
   * @returns {Promise<PromotionInformation[]>} Processed promotions
   */
  const fetchFreeShippingPromotionsByMarket = async (market: string) => {
    const promotions = await $fetch<PromotionsResponse>(
      `/api/cl/getPromotions/${market}`
    )
    return promotions ? mapPromotions(promotions) : []
  }

  /**
   * Computed property for free shipping promotions in the current market
   *
   * @type {ComputedRef<PromotionInformation[]>}
   */
  const freeShippingPromotions = computed(() => {
    if (!data.value || !Array.isArray(data.value)) {
      return []
    }

    // Filter promotions that have the "free-shipping" tag
    const filteredPromotions = data.value.filter(promotion => {
      return (
        promotion.active === true &&
        promotion.tags?.some(tag => tag.name === 'free-shipping')
      )
    })
    return mapPromotions(filteredPromotions)
  })

  /**
   * Computed property for the currently active free shipping promotion
   * Returns the first active promotion if multiple are available
   *
   * @type {ComputedRef<PromotionInformation | null>}
   */
  const activeFreeShippingPromotion = computed<PromotionInformation | null>(
    () =>
      freeShippingPromotions.value.find(promotion => promotion.isActive) || null
  )

  return {
    freeShippingPromotions,
    activeFreeShippingPromotion,
    fetchFreeShippingPromotionsByMarket,
  }
}
